import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconDeviceFloppy,
  IconEdit,
  IconEye,
  IconFileDescription,
  IconFilePlus,
  IconPlus,
  IconSearch,
  IconSend,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  getLocalDate,
  listReleaseNotes,
  makeChangeItem,
  makeRelease,
  releaseErrorMessage,
  RELEASE_MODULES,
  RELEASE_NOTE_TYPES,
  removeReleaseNote,
  subscribeReleaseNotes,
  upsertReleaseNote,
} from "./releaseNotesStore";

// Projenizde yönetim ve kullanıcı sürüm-notu route'ları farklıysa burayı değiştirin.
const RETURN_PATH = "/";
const PUBLIC_RELEASE_NOTES_PATH = "/release-notes";

const TYPE_DATA = RELEASE_NOTE_TYPES.map(({ value, label }) => ({ value, label }));
const TYPE_MAP = Object.fromEntries(RELEASE_NOTE_TYPES.map((item) => [item.value, item]));

const formatDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "—";
  return new Date(`${value}T12:00:00`).toLocaleDateString("tr-TR", {
    day: "numeric", month: "long", year: "numeric",
  });
};

function validateRelease(form, all, targetStatus) {
  const errors = {};
  const version = form.version.trim().replace(/^v/i, "");
  if (!version) errors.version = "Sürüm numarası zorunludur.";
  else if (!/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(version)) {
    errors.version = "Örnek format: 1.4.0 veya 1.4.0-beta.1";
  } else if (all.some((release) => String(release.id) !== String(form.id) &&
    String(release.version).replace(/^v/i, "").toLowerCase() === version.toLowerCase())) {
    errors.version = "Bu sürüm numarası daha önce kullanılmış.";
  }
  if (!form.title.trim()) errors.title = "Sürüm başlığı zorunludur.";
  if (targetStatus === "PUBLISHED") {
    if (!form.summary.trim()) errors.summary = "Yayınlamak için kısa bir özet girin.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.publishedAt || "") ||
      Number.isNaN(new Date(`${form.publishedAt}T12:00:00`).getTime())) {
      errors.publishedAt = "Geçerli bir yayın tarihi seçin.";
    } else if (form.publishedAt > getLocalDate()) {
      errors.publishedAt = "İleri tarihli yayınlama henüz desteklenmiyor.";
    }
    if (form.items.length === 0) errors.items = "Yayınlamak için en az bir değişiklik ekleyin.";
    form.items.forEach((item, index) => {
      if (!item.title.trim() || !item.description.trim() || !item.module.trim()) {
        errors.items = `${index + 1}. değişiklikte başlık, açıklama ve modül zorunludur.`;
      }
    });
  }
  return errors;
}

function StatBox({ label, value, color = "indigo" }) {
  return (
    <Paper withBorder radius="lg" p="md">
      <Text size="xs" c="dimmed" fw={600}>{label}</Text>
      <Text size="xl" fw={800} c={color} mt={3}>{value}</Text>
    </Paper>
  );
}

function ChangeEditor({ item, index, onChange, onRemove, error }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon variant="light" color="indigo" radius="xl" size="sm">
            <Text fw={750} size="xs">{index + 1}</Text>
          </ThemeIcon>
          <Text fw={650} size="sm">Değişiklik</Text>
        </Group>
        <Tooltip label="Bu değişikliği kaldır">
          <ActionIcon variant="subtle" color="red" aria-label="Değişikliği sil" onClick={onRemove}>
            <IconTrash size={17}/>
          </ActionIcon>
        </Tooltip>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="sm">
        <Select
          label="Değişiklik türü"
          data={TYPE_DATA}
          value={item.type}
          onChange={(value) => onChange("type", value || "NEW")}
          allowDeselect={false}
        />
        <TextInput
          label="Modül"
          placeholder="Örn. BT Destek"
          list={`module-choices-${item.id}`}
          value={item.module}
          onChange={(event) => onChange("module", event.currentTarget.value)}
          error={error && !item.module.trim() ? "Modül girin" : null}
        />
      </SimpleGrid>
      <datalist id={`module-choices-${item.id}`}>
        {RELEASE_MODULES.map((name) => <option value={name} key={name} />)}
      </datalist>
      <TextInput
        label="Değişiklik başlığı"
        placeholder="Örn. İzin taleplerine filtre eklendi"
        mb="sm"
        value={item.title}
        onChange={(event) => onChange("title", event.currentTarget.value)}
        error={error && !item.title.trim() ? "Başlık girin" : null}
      />
      <Textarea
        label="Açıklama"
        placeholder="Kullanıcının göreceği kısa, anlaşılır açıklama..."
        minRows={2}
        autosize
        value={item.description}
        onChange={(event) => onChange("description", event.currentTarget.value)}
        error={error && !item.description.trim() ? "Açıklama girin" : null}
      />
    </Paper>
  );
}

export default function ReleaseNotesAdminPage() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => makeRelease());
  const [selectedId, setSelectedId] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(form));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);

  const refreshReleases = async () => {
    try {
      const entries = await listReleaseNotes();
      setReleases(entries);
    } catch (error) {
      setNotice({ color: "red", text: `Sürüm listesi alınamadı: ${releaseErrorMessage(error)}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshReleases();
    return subscribeReleaseNotes(refreshReleases);
  }, []);

  const sorted = useMemo(() => [...releases].sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))), [releases]);
  const filtered = useMemo(() => sorted.filter((release) => {
    const matchStatus = statusFilter === "ALL" || release.status === statusFilter;
    const query = search.trim().toLocaleLowerCase("tr-TR");
    const matchQuery = !query || [release.version, release.title, release.summary]
      .join(" ").toLocaleLowerCase("tr-TR").includes(query);
    return matchStatus && matchQuery;
  }), [sorted, search, statusFilter]);

  const isDirty = savedSnapshot !== null && JSON.stringify(form) !== savedSnapshot;
  const warnBeforeDiscard = () =>
    !isDirty || window.confirm("Kaydedilmemiş değişiklikler silinecek. Devam edilsin mi?");

  const selectRelease = (release) => {
    if (!warnBeforeDiscard()) return;
    const next = structuredCloneSafe(release);
    setForm(next);
    setSelectedId(release.id);
    setSavedSnapshot(JSON.stringify(next));
    setErrors({});
    setNotice(null);
  };

  const createNew = () => {
    if (!warnBeforeDiscard()) return;
    const next = makeRelease();
    setForm(next);
    setSelectedId(null);
    setSavedSnapshot(JSON.stringify(next));
    setErrors({});
    setNotice(null);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setNotice(null);
  };
  const updateItem = (itemId, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item),
    }));
    setErrors((current) => ({ ...current, items: undefined }));
    setNotice(null);
  };

  const save = async (status) => {
    if (saving) return;
    if (form.status === "PUBLISHED" && status === "DRAFT" &&
      !window.confirm("Bu sürüm yayından kaldırılacak ve kullanıcıların ekranından kaybolacak. Devam edilsin mi?")) {
      return;
    }
    const next = {
      ...form,
      version: form.version.trim().replace(/^v/i, ""),
      status,
      publishedAt: form.publishedAt || getLocalDate(),
      // Yayına alınan sürümde eksik değişiklik kalamaz; taslakta boş satırları koruyoruz.
      items: form.items,
    };
    const nextErrors = validateRelease(next, releases, status);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setNotice({ color: "red", text: "Formdaki eksik veya hatalı alanları kontrol edin." });
      return;
    }
    setSaving(true);
    try {
      const saved = await upsertReleaseNote(next);
      await refreshReleases();
      setForm(saved);
      setSelectedId(saved.id);
      setSavedSnapshot(JSON.stringify(saved));
      setErrors({});
      setNotice({ color: "teal", text: status === "PUBLISHED"
        ? "Sürüm yayınlandı. Kullanıcı Sürüm Notları sayfasında görüntülenebilir."
        : "Sürüm taslak olarak kaydedildi." });
    } catch (error) {
      setNotice({ color: "red", text: releaseErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const deleteCurrent = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (selectedId) await removeReleaseNote(selectedId);
      await refreshReleases();
      const blank = makeRelease();
      setForm(blank);
      setSavedSnapshot(JSON.stringify(blank));
      setSelectedId(null);
      setErrors({});
      setNotice({ color: "teal", text: "Sürüm kaydı silindi." });
      setDeleteOpened(false);
    } catch (error) {
      setNotice({ color: "red", text: releaseErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const selectedHasBeenSaved = selectedId && releases.some((r) => r.id === selectedId);

  return (
    <Box bg="var(--mantine-color-body)" mih="100vh" py={{ base: "lg", md: 34 }}>
      <Container size="xl">
        <Group justify="space-between" align="center" mb="md" gap="sm">
          <Text size="sm" c="dimmed" fw={500}>SMS Grup Portal / Sürüm Yönetimi</Text>
          <Group gap="xs">
            <Button variant="subtle" color="gray" size="sm" leftSection={<IconEye size={16} />}
              onClick={() => navigate(PUBLIC_RELEASE_NOTES_PATH)}>Kullanıcı görünümü</Button>
            <Button variant="light" color="indigo" size="sm" leftSection={<IconArrowLeft size={16} />}
              onClick={() => { if (warnBeforeDiscard()) navigate(RETURN_PATH); }}>Ana Sayfaya Dön</Button>
          </Group>
        </Group>

        <Paper radius="xl" p={{ base: "xl", md: 32 }} mb="xl"
          style={{ background: "linear-gradient(115deg,#19275c 0%,#314ba0 63%,#466ab8 100%)", color: "white" }}>
          <Group justify="space-between" align="center" gap="lg">
            <Box>
              <Group gap="sm" mb="xs">
                <ThemeIcon variant="white" color="indigo" radius="md" size={42}><IconFileDescription size={23}/></ThemeIcon>
                <Text fw={700} size="sm" style={{ letterSpacing: 1 }}>SMS GRUP PORTAL</Text>
              </Group>
              <Title order={1} size="h2" style={{ color: "white" }}>Sürüm Notları Yönetimi</Title>
              <Text mt="xs" style={{ color: "rgba(255,255,255,.85)" }}>
                Yenilikleri taslak olarak hazırlayın, düzenleyin ve kullanıcılara yayınlayın.
              </Text>
            </Box>
            <Button color="white" c="indigo" leftSection={<IconFilePlus size={18} />} onClick={createNew}>
              Yeni Sürüm Oluştur
            </Button>
          </Group>
        </Paper>

        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="xl" spacing="md">
          <StatBox label="Toplam sürüm" value={releases.length}/>
          <StatBox label="Yayınlanan" value={releases.filter((r) => r.status === "PUBLISHED").length} color="teal"/>
          <StatBox label="Taslak" value={releases.filter((r) => r.status !== "PUBLISHED").length} color="orange"/>
          <StatBox label="Değişiklik kaydı" value={releases.reduce((sum, r) => sum + r.items.length, 0)} color="blue"/>
        </SimpleGrid>

        {loading && <Alert mb="lg" color="blue">Sürüm notları sunucudan yükleniyor...</Alert>}

        <Grid gutter="lg" align="flex-start">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper withBorder radius="lg" p="lg">
              <Group justify="space-between" mb="md">
                <Title order={3} size="h4">Sürüm kayıtları</Title>
                <Badge variant="light" color="gray">{filtered.length}</Badge>
              </Group>
              <TextInput placeholder="Sürüm veya başlık ara..." leftSection={<IconSearch size={16} />}
                mb="sm" value={search} onChange={(event) => setSearch(event.currentTarget.value)} />
              <Select mb="md" allowDeselect={false} value={statusFilter}
                onChange={(value) => setStatusFilter(value || "ALL")}
                data={[{ value: "ALL", label: "Tüm durumlar" },
                  { value: "PUBLISHED", label: "Yayında" }, { value: "DRAFT", label: "Taslak" }]}/>
              <Divider mb="sm"/>
              <ScrollArea.Autosize mah={650} offsetScrollbars>
                {filtered.length ? <Stack gap="xs">
                  {filtered.map((release) => (
                    <Paper key={release.id} p="sm" radius="md" withBorder
                      onClick={() => selectRelease(release)}
                      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault(); selectRelease(release);
                      } }}
                      role="button" tabIndex={0} aria-pressed={String(selectedId) === String(release.id)}
                      style={{ cursor: "pointer", borderColor: String(selectedId) === String(release.id)
                        ? "var(--mantine-color-indigo-5)" : undefined,
                        background: String(selectedId) === String(release.id)
                          ? "var(--mantine-color-indigo-light)" : undefined }}>
                      <Group justify="space-between" mb={6}>
                        <Text fw={750} size="sm">v{release.version}</Text>
                        <Badge size="xs" variant="light" color={release.status === "PUBLISHED" ? "teal" : "orange"}>
                          {release.status === "PUBLISHED" ? "Yayında" : "Taslak"}
                        </Badge>
                      </Group>
                      <Text size="sm" fw={600} lineClamp={2}>{release.title}</Text>
                      <Group justify="space-between" mt="xs">
                        <Text c="dimmed" size="xs">{release.items.length} değişiklik</Text>
                        <Text c="dimmed" size="xs">{formatDate((release.updatedAt || "").slice(0, 10))}</Text>
                      </Group>
                    </Paper>
                  ))}
                </Stack> : <Stack gap="xs" align="center" py="xl">
                  <IconFileDescription color="gray" size={27}/>
                  <Text size="sm" c="dimmed" ta="center">Kayıt bulunamadı. Yeni sürüm oluşturabilirsiniz.</Text>
                </Stack>}
              </ScrollArea.Autosize>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8 }}>
            <Paper withBorder radius="lg" p={{ base: "lg", sm: "xl" }}>
              <Group justify="space-between" align="start" mb="lg">
                <Box>
                  <Group gap="xs">
                    <Title order={2} size="h3">{selectedHasBeenSaved ? "Sürümü düzenle" : "Yeni sürüm"}</Title>
                    <Badge variant="light" color={form.status === "PUBLISHED" ? "teal" : "orange"}>
                      {form.status === "PUBLISHED" ? "Yayında" : "Taslak"}
                    </Badge>
                    {isDirty && <Badge color="yellow" variant="dot">Kaydedilmedi</Badge>}
                  </Group>
                  <Text c="dimmed" size="sm" mt={4}>Sürüm bilgilerini ve değişiklikleri girin.</Text>
                </Box>
                {selectedHasBeenSaved && <Tooltip label="Sürümü sil">
                  <ActionIcon color="red" variant="light" size="lg" aria-label="Sürümü sil"
                    onClick={() => setDeleteOpened(true)}><IconTrash size={19}/></ActionIcon>
                </Tooltip>}
              </Group>
              {notice && <Alert color={notice.color} radius="md" mb="lg"
                icon={notice.color === "red" ? <IconAlertCircle size={18}/> : <IconCheck size={18}/>}>
                {notice.text}
              </Alert>}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
                <TextInput label="Sürüm numarası" placeholder="1.4.0" required
                  value={form.version} onChange={(event) => updateForm("version", event.currentTarget.value)}
                  error={errors.version}/>
                <TextInput label="Yayın tarihi" type="date" max={getLocalDate()} required={form.status === "PUBLISHED"}
                  value={form.publishedAt} onChange={(event) => updateForm("publishedAt", event.currentTarget.value)}
                  error={errors.publishedAt}/>
              </SimpleGrid>
              <TextInput mb="md" label="Sürüm başlığı" placeholder="Örn. Daha hızlı ve kullanışlı bir Portal"
                required value={form.title} onChange={(event) => updateForm("title", event.currentTarget.value)} error={errors.title}/>
              <Textarea label="Genel özet" placeholder="Bu sürümde neler değişti? Kullanıcıların anlayacağı şekilde özetleyin."
                minRows={3} autosize mb="lg" value={form.summary}
                onChange={(event) => updateForm("summary", event.currentTarget.value)} error={errors.summary}/>

              <Divider my="lg"/>
              <Group justify="space-between" mb="md">
                <Box>
                  <Title order={3} size="h4">Değişiklikler</Title>
                  <Text size="xs" c="dimmed">Her yenilik, iyileştirme veya hata düzeltmesi ayrı kayıt olmalı.</Text>
                </Box>
                <Button variant="light" leftSection={<IconPlus size={17}/>} size="sm"
                  onClick={() => { updateForm("items", [...form.items, makeChangeItem()]); }}>Değişiklik ekle</Button>
              </Group>
              {errors.items && <Alert icon={<IconAlertCircle size={16}/>} color="red" mb="sm">{errors.items}</Alert>}
              {form.items.length ? <Stack gap="md">
                {form.items.map((item, index) => (
                  <ChangeEditor key={item.id} index={index} item={item} error={Boolean(errors.items)}
                    onChange={(field, value) => updateItem(item.id, field, value)}
                    onRemove={() => updateForm("items", form.items.filter((x) => x.id !== item.id))}/>
                ))}
              </Stack> : <Paper p="xl" withBorder radius="md" ta="center">
                <Text size="sm" c="dimmed">Henüz bir değişiklik eklenmedi.</Text>
              </Paper>}
              <Divider my="xl"/>
              <Group justify="space-between" gap="sm" wrap="wrap">
                <Button variant="subtle" color="gray" leftSection={<IconEye size={17}/>}
                  onClick={() => setPreviewOpened(true)}>Önizleme</Button>
                <Group gap="xs">
                  <Button variant="light" color="gray" leftSection={<IconDeviceFloppy size={17}/>}
                    onClick={() => save("DRAFT")} loading={saving}>
                    {form.status === "PUBLISHED" ? "Yayından Kaldır / Taslak" : "Taslak Kaydet"}
                  </Button>
                  <Button color="indigo" leftSection={form.status === "PUBLISHED" ? <IconEdit size={17}/> : <IconSend size={17}/>}
                    onClick={() => save("PUBLISHED")} loading={saving}>
                    {form.status === "PUBLISHED" ? "Değişiklikleri Kaydet" : "Yayınla"}
                  </Button>
                </Group>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>

      <Modal opened={previewOpened} onClose={() => setPreviewOpened(false)} title="Sürüm önizlemesi"
        size="lg" centered radius="lg">
        <Group gap="xs" mb="sm">
          <Badge color="indigo" variant="light">v{form.version || "?.?.?"}</Badge>
          <Badge color={form.status === "PUBLISHED" ? "teal" : "orange"} variant="light">
            {form.status === "PUBLISHED" ? "Yayında" : "Taslak"}
          </Badge>
          <Text size="xs" c="dimmed">{formatDate(form.publishedAt)}</Text>
        </Group>
        <Title order={3} mb="xs">{form.title || "Sürüm başlığı"}</Title>
        <Text size="sm" c="dimmed" mb="lg">{form.summary || "Sürüm özeti henüz eklenmedi."}</Text>
        <Divider mb="lg"/>
        <Stack gap="lg">
          {form.items.map((item) => (
            <Box key={item.id}>
              <Group gap="xs" mb={6}>
                <Badge variant="light" color={TYPE_MAP[item.type]?.color || "gray"}>
                  {TYPE_MAP[item.type]?.label || item.type}
                </Badge>
                <Badge variant="outline" color="gray">{item.module || "Modül"}</Badge>
              </Group>
              <Text size="sm" fw={650}>{item.title || "Değişiklik başlığı"}</Text>
              <Text size="sm" c="dimmed">{item.description || "Açıklama henüz girilmedi."}</Text>
            </Box>
          ))}
          {!form.items.length && <Text size="sm" c="dimmed">Henüz değişiklik eklenmedi.</Text>}
        </Stack>
        <Button mt="xl" variant="light" fullWidth leftSection={<IconX size={16}/>}
          onClick={() => setPreviewOpened(false)}>Kapat</Button>
      </Modal>

      <Modal opened={deleteOpened} onClose={() => setDeleteOpened(false)} title="Sürüm kaydını sil"
        centered radius="lg">
        <Text size="sm">v{form.version} sürümünü kalıcı olarak silmek istediğinizden emin misiniz?</Text>
        <Text c="dimmed" size="sm" mt="xs">Bu işlem mevcut tarayıcıdaki kayıt için geri alınamaz.</Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setDeleteOpened(false)}>Vazgeç</Button>
          <Button color="red" leftSection={<IconTrash size={17}/>} onClick={deleteCurrent} loading={saving}>Sürümü Sil</Button>
        </Group>
      </Modal>
    </Box>
  );
}

function structuredCloneSafe(value) {
  return JSON.parse(JSON.stringify(value));
}
